import { Box, Button, CircularProgress, Divider, Tooltip, Typography, useTheme } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from "react"
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import CreateRoom from '../../components/webinar-room/CreateOnlineMeetingDialog'
import HybridClassStartOptionsDialog from '../../components/dashboard-components/HybridClassStartOptionsDialog'
import ConflictingClassSessionDetailsDrawer from '../../components/dashboard-components/ConflictingClassSessionDetailsDrawer'
import AddQuizFromClassDialog from '../../components/exams-components/quiz-questionbank/class-quizzes/AddQuizFromClassDialog'
import TryAgainMessageBlock from '../../components/common/TryAgainMessageBlock'
import { getTransformedMeetingType } from '../../utils/meeting-utilities.js'
import DataLoadingSpinner from '../../components/common/DataLoadingSpinner'
import CustomTabs from '../../components/common/CustomTabs.js'
import { formatDate, getClassTitle, Icons } from '../../utils/utilities'
import noMeetingsImg from '../../assets/no-meetings.svg'
import { useUserStore } from '../../store/user.store.js'
import { useRoomStore } from '../../store/room.store.js'
import api from '../../service/api.js'
import { useOnlineSessionsStore } from '../../store/onlinesessions.store.jsx'
import { observer } from 'mobx-react'
import dayjs from 'dayjs'
import CustomChip from '../../components/common/CustomChip.js'


const getCurrentWeekDates = () => {
    const today = dayjs()
    const daysFromMonday = ( today.day() + 6 ) % 7
    const monday = today.subtract( daysFromMonday, 'day' )
    return Array.from( { length: 7 }, ( _, i ) => monday.add( i, 'day' ).format( "YYYY-MM-DD" ) )
}

const MeetingCard = ( { meeting, userRoleForLinks } ) => {

    const { palette, border, shadows, layout } = useTheme()

    return (
        <Box borderRadius={layout.borderRadius} minWidth={0} boxShadow={shadows[1]} bgcolor={palette.contentBg} padding="10px" key={meeting.id}>
            <CustomChip textStyles={{ textTransform: "uppercase" }} value={meeting.meeting_type} />
            <CustomChip containerStyles={{ ml: 1 }} color={meeting.can_join ? "success" : meeting.ended ? "error" : !meeting.started ? "secondary" : ""} value={meeting.can_join ? "Active" : meeting.ended ? "Ended" : !meeting.started ? "Not yet started" : ""} />
            <Typography gap="5px" alignItems="center" marginTop="10px" noWrap variant='h6' fontSize="16px"  > {meeting.title} </Typography>
            <Typography display="flex" gap="5px" fontSize="12px" alignItems="center" noWrap variant='subtitle2'> {Icons.default.Calendar} {formatDate( meeting.starts_at, { format: "DD-MM-YYYY, hh:mm A" } )} - {formatDate( meeting.ends_at, { format: "hh:mm A" } )} </Typography>
            <Box display="flex" gap="10px" alignItems="center" flexWrap="wrap" marginTop="10px">
                {meeting.meeting_type !== 'hybrid-class' && !meeting.ended && <Link to={`/${getTransformedMeetingType( meeting?.meeting_type )}/${meeting.id}`}><Button disabled={!meeting.can_join} size='small' sx={{ borderRadius: "5px" }} disableElevation variant='contained' color='primary' startIcon={Icons.default.Login} >Join</Button></Link>}
                {meeting.meeting_type !== 'hybrid-class' && meeting.ended && <Button disabled={meeting.ended} size='small' sx={{ borderRadius: "5px" }} disableElevation variant='contained' color='primary' startIcon={Icons.default.Login} >Join</Button>}
                <Link to={`/${userRoleForLinks}/${getTransformedMeetingType( meeting?.meeting_type )}/${meeting.id}`} ><Button size='small' sx={{ borderRadius: "5px" }} startIcon={Icons.default.Info} >View Class</Button></Link>
            </Box>
        </Box >
    )
}

const ScheduledHybridClassCard = ( { room, starting = false, checking = false, onStart, userRoleForLinks } ) => {
    const { palette, border, shadows, layout } = useTheme()
    const exists = room.does_exists

    if ( exists && !dayjs().isBefore( dayjs( exists.ends_at ) ) )
        return null


    return (
        <Box borderRadius={layout.borderRadius} minWidth={0} boxShadow={shadows[1]} bgcolor={palette.contentBg} padding="10px">
            <Box display="flex" alignItems="center" gap="5px">
                <CustomChip value="HYBRID-CLASS" />
                <CustomChip color={exists ? "secondary" : "error"} value={exists ? "Started" : "Scheduled"} />
            </Box>
            <Tooltip title={getClassTitle( room )}>
                <Typography gap="5px" alignItems="center" marginTop="10px" noWrap variant='h6' fontSize="16px">{getClassTitle( room )}</Typography>
            </Tooltip>
            <Typography display="flex" gap="5px" fontSize="12px" alignItems="center" noWrap variant='subtitle2'> {Icons.default.Calendar} {formatDate( room.selected_date, { format: "DD-MM-YYYY" } )} {formatDate( `${room.selected_date}T${room.sTime}`, { format: "hh:mm A" } )} - {formatDate( `${room.selected_date}T${room.eTime}`, { format: "hh:mm A" } )} </Typography>
            <Box display="flex" gap="10px" alignItems="center" flexWrap="wrap" marginTop="10px">
                {exists
                    ? <Link to={`/${userRoleForLinks}/hybrid-class/${exists.id}`}><Button size='small' sx={{ borderRadius: "5px" }} disableElevation color='secondary' variant='outlined' startIcon={Icons.default.Info}>View Class</Button></Link>
                    : <Button size='small' sx={{ borderRadius: "5px" }} disableElevation variant='contained' color='primary' endIcon={( starting || checking ) ? <CircularProgress size={14} color='inherit' /> : Icons.default.ArrowForward} disabled={starting || checking} onClick={onStart} >{checking ? "Checking for conflicts..." : starting ? "Starting..." : "Start Class"}</Button>
                }
            </Box>
        </Box >
    )
}

const meetingTypes = [
    { label: "Webinars", value: "webinar" },
    { label: "Discussion meetings", value: "discussion" }
]

const OnlineMeetings = observer( ( { meetingType = "meetings" } ) => {

    const [inCreateMode, setInCreateMode] = useState( false )
    const [startingClassRooms, setStartingClassRooms] = useState( {} )
    const [roomsLoading, setRoomsLoading] = useState( false )
    const [selectedHybridClassToStart, setSelectedHybridClassToStart] = useState( null )
    const [roomSelectedForQuiz, setRoomSelectedForQuiz] = useState( null )
    const [checkingConflictForId, setCheckingConflictForId] = useState( null )
    const [conflictingSessionDetails, setConflictingSessionDetails] = useState( null )

    const { palette, layout } = useTheme()

    const [searchParams, setSearchParams] = useSearchParams( { sessionType: meetingType === "meetings" ? "webinar" : meetingType === "hybrid-class" ? "hybrid-class" : "class" } )
    const sessionType = searchParams.get( 'sessionType' ) || ( meetingType === "meetings" ? "webinar" : meetingType === "hybrid-class" ? "hybrid-class" : "class" )


    const setSessionType = ( value ) => {
        setSearchParams( prev => {
            const newValue = new URLSearchParams( prev )
            newValue.set( 'sessionType', value )
            return newValue
        }, { replace: true } )
    }

    const OnlineSessionsStore = useOnlineSessionsStore()
    const RoomStore = useRoomStore()
    const UserStore = useUserStore()

    const moduleHeading = meetingType === 'class' ? "Online Classes" : meetingType !== 'hybrid-class' ? "Webinars and Meetings" : "Hybrid Classes"
    const moduleSubHeading = meetingType === 'class' ? "Check your online classes details from here." : meetingType !== 'hybrid-class' ? "Manage vitual sessions from here." : "Check your hybrid class details and recordings."
    const classTypeSession = meetingType.includes( 'class' )
    const isStaff = ["FACULTY", "HOD", "PRINCIPAL"].includes( UserStore.getUser.user_role )

    const error = OnlineSessionsStore.getErrors?.onlineSessions
    const { refreshing, initialLoad: loading } = OnlineSessionsStore.getSessionsLoadingStatus

    const scheduledHybridClasses = useMemo( () => {
        if ( !isStaff || sessionType !== "hybrid-class" ) return []
        const weekDates = getCurrentWeekDates()
        const weeklyRooms = []
        for ( const date of weekDates ) weeklyRooms.push( ...( RoomStore.getRooms[date] || [] ) )
        const meetings = OnlineSessionsStore.getOnlineSessions
        return weeklyRooms
            .map( room => {
                const does_exists = meetings.find( meeting => meeting.time_table_id === room.time_table_id )
                const mode = does_exists?.meeting_type === 'hybrid-class' ? "hybrid" : ( room.online && room.is_hybrid_course ) ? "hybrid" : null
                return { ...room, does_exists, mode }
            } )
            .filter( room => room.mode === "hybrid" )
            .sort( ( a, b ) => ( a.selected_date + a.sTime ).localeCompare( b.selected_date + b.sTime ) )
    }, [sessionType, isStaff, RoomStore.getRooms, OnlineSessionsStore.getOnlineSessions] )

    const fetchClassTimetable = useCallback( async () => {
        if ( !isStaff || sessionType !== "hybrid-class" ) return
        const weekDates = getCurrentWeekDates()
        if ( weekDates.some( d => !RoomStore.getRooms[d] ) ) {
            setRoomsLoading( true )
            await RoomStore.fetchRooms( weekDates )
            setRoomsLoading( false )
        }
    }, [isStaff, sessionType, RoomStore] )

    const fetchMeetings = useCallback( async () => {
        await OnlineSessionsStore.fetchOnlineSessions()
    }, [OnlineSessionsStore] )

    const startHybridClass = async ( room, meeting_type = "hybrid-class" ) => {
        try {
            setStartingClassRooms( prev => ( { ...prev, [room.time_table_id]: true } ) )
            if ( room["sTime"] === undefined || room["sTime"] === null || room["eTime"] === undefined || room["eTime"] === null ) {
                toast.error( "ERP not sending Start Time / End Time" )
                return
            }
            delete room.status
            room.record = false
            let groupTitle
            if ( room.batch_short_name !== "NA" ) {
                groupTitle = `${room.course_branch_short_name}-${room.course_branch_short_name || ""}${room.current_year ? ` Y${room.current_year}` : ``}${room.current_sem ? ` S${room.current_sem}` : ``} (${room.section_short_name + ")"}`
            } else {
                groupTitle = `${room.batch_short_name}-${room.course_branch_short_name || ""}${room.current_year ? ` Y${room.current_year}` : ``}${room.current_sem ? ` S${room.current_sem}` : ``}`
            }
            const onlineClassPayload = {
                title: getClassTitle( room ),
                starts_at: dayjs( `${room.selected_date}T${room.sTime}` ),
                ends_at: dayjs( `${room.selected_date}T${room.eTime}` ),
                hosts: [],
                selected_student_groups: [room ? room.batch_short_name !== "NA" ?
                    { title: groupTitle, course_name_short: room.course_name_short, current_sem: room.current_sem, current_year: room.current_year, id: String( room.batch_assignment_id ), batch_short_name: room.batch_short_name, batch_assignment_id: room.batch_assignment_id, branch_name: room.branch_name, branch_id: room.branch_id } :
                    { title: groupTitle, course_branch_short_name: room.course_branch_short_name, current_sem: room.current_sem, current_year: room.current_year, id: `${room.section_assignment_id}-${room.section_short_name}`, section_assignment_id: room.section_assignment_id, section_short_name: room.section_short_name, branch_name: room.branch_name, branch_id: room.branch_id } : '-',],
                added_users: [],
                added_users_emails: [],
                batch_groups_selected: room.batch_short_name !== "NA" ? 1 : 0,
                section_groups_selected: room.batch_short_name !== "NA" ? 0 : 1,
                selectedAcYear: null,
                meeting_type: meeting_type,
                meeting_settings: {
                    access_type: 'SPECIFIED_GROUPS_AND_USERS',
                    any_one_can_join_with_pass_key: false,
                    enable_chats: false,
                    enable_screen_sharing: false,
                    enable_microphone: false,
                    enable_video_feed: false,
                    join_without_host: false,
                    enable_reactions: false,
                },
                time_table_id: room.time_table_id,
                time_table_class_details: room
            }
            const { data: { data: newRoom } } = await api.onlineMeetings.createMeeting( onlineClassPayload )
            await fetchMeetings()
            const userRole = UserStore.getUser.user_role === "STUDENT" ? "student" : "faculty"
            window.open( `/${userRole}/hybrid-class/${newRoom.id}` )
        } catch ( err ) {
            if ( err?.response?.data?.alreadyExists ) {
                const meeting = err?.response?.data?.data
                const userRole = UserStore.getUser.user_role === "STUDENT" ? "student" : "faculty"
                window.open( `/${userRole}/hybrid-class/${meeting?.id}` )
            } else {
                toast( err?.response?.data?.message || "Failed to start the class, please try again." )
            }
        } finally {
            setStartingClassRooms( prev => ( { ...prev, [room.time_table_id]: false } ) )
        }
    }

    const showStartClassOptions = async ( room ) => {
        try {
            setCheckingConflictForId( room.time_table_id )
            const { data: { data: conflictDetails } } = await api.onlineMeetings.checkConflictingClassSession( room.time_table_id )

            if ( conflictDetails?.conflict_found ) {
                setConflictingSessionDetails( conflictDetails )
                return
            }

            setSelectedHybridClassToStart( room )
        } catch ( err ) {
            toast( err?.response?.data?.message || "Something went wrong while checking for conflicting sessions." )
        } finally {
            setCheckingConflictForId( null )
        }
    }

    useEffect( () => {
        fetchMeetings()
        fetchClassTimetable()
    }, [fetchMeetings, fetchClassTimetable] )


    return (
        <Box padding={layout.pagePadding} display="flex" flexDirection="column" overflow="auto" flexGrow={1}>

            {isStaff && <CreateRoom defaultType={!classTypeSession ? sessionType : null} fetchMeetings={fetchMeetings} open={inCreateMode} setOpen={setInCreateMode} />}
            {Boolean( selectedHybridClassToStart ) && <HybridClassStartOptionsDialog onQuizDialogOpen={() => setRoomSelectedForQuiz( selectedHybridClassToStart )} onStart={async ( options ) => await startHybridClass( selectedHybridClassToStart, options.meeting_type )} open={Boolean( selectedHybridClassToStart )} setOpen={setSelectedHybridClassToStart} room={selectedHybridClassToStart} />}
            {roomSelectedForQuiz && <AddQuizFromClassDialog fetchQuizzesInstances={() => fetchMeetings()} state={Boolean( roomSelectedForQuiz )} room={roomSelectedForQuiz} setState={setRoomSelectedForQuiz} />}
            {Boolean( conflictingSessionDetails ) && <ConflictingClassSessionDetailsDrawer open={Boolean( conflictingSessionDetails )} onClose={() => setConflictingSessionDetails( null )} details={conflictingSessionDetails} />}
            <Box display="flex" gap={layout.cardsGap} flexWrap="wrap" alignItems="flex-end" justifyContent="space-between">
                <Box>
                    <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">{moduleHeading}</Typography>
                    <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >{moduleSubHeading}</Typography>
                </Box>
                {isStaff && !classTypeSession && <Button sx={{ flexGrow: { xs: 1, sm: "initial" } }} startIcon={Icons.default.AddIcon} disableElevation onClick={() => setInCreateMode( true )} variant='contained' >Create {sessionType !== 'class' ? sessionType : ""}</Button>}
            </Box>

            <Box flexGrow={1} sx={{ marginTop: !classTypeSession ? "20px" : "10px" }} >
                {!classTypeSession && <CustomTabs tabs={meetingTypes} value={sessionType} handleTabChange={setSessionType} />}

                {sessionType === "hybrid-class" && !error && isStaff && <Box marginTop="15px">
                    <Typography variant="subtitle2" component="h2" display="flex" gap="5px" alignItems="center" fontWeight="600" fontSize="14px">{Icons.default.Calendar} Upcoming and Active Classes</Typography>
                    {roomsLoading && <Box marginTop="10px"><DataLoadingSpinner waitingMessage="Loading scheduled hybrid classes..." /></Box>}
                    {!roomsLoading && scheduledHybridClasses.length > 0 && <Box marginTop="10px" gap={layout.cardsGap} display="grid" gridTemplateColumns={{ lg: "repeat(4,1fr)", md: "repeat(3,1fr)", sm: "repeat(2,1fr)", xs: "1fr" }} >
                        {scheduledHybridClasses.map( room => <ScheduledHybridClassCard key={`${room.time_table_id}-${room.selected_date}`} room={room} starting={startingClassRooms[room.time_table_id]} checking={checkingConflictForId === room.time_table_id} onStart={() => showStartClassOptions( room )} userRoleForLinks="faculty" /> )}
                    </Box>}
                    {!roomsLoading && scheduledHybridClasses.length === 0 && <Box display="flex" padding="15px" borderRadius={layout.borderRadius} bgcolor={palette.contentBg} alignItems="center" justifyContent="center" marginTop="10px" >
                        <Typography variant='subtitle2'>No hybrid classes scheduled for this week.</Typography>
                    </Box>}
                </Box>}
                {sessionType === "hybrid-class" && !error && isStaff && <Divider sx={{ margin: "20px 0 5px 0" }} />}
                {sessionType === "hybrid-class" && !error && isStaff && <Box marginTop="10px">
                    <Typography variant="subtitle2" component="h2" display="flex" gap="5px" alignItems="center" fontWeight="600" fontSize="14px">{Icons.default.HybridClasses} Completed Hybrid Classes</Typography>
                </Box>}

                {!error && OnlineSessionsStore.getOnlineSessions.filter( m => m.meeting_type === sessionType ).length > 0 && <Box marginTop="10px" gap={layout.cardsGap} display="grid" gridTemplateColumns={{ lg: "repeat(4,1fr)", md: "repeat(3,1fr)", sm: "repeat(2,1fr)", xs: "1fr" }} >
                    {OnlineSessionsStore.getOnlineSessions.filter( m => m.meeting_type === sessionType ).map( meeting => {

                        if ( sessionType === "hybrid-class" && dayjs().isBefore( dayjs( meeting.ends_at ) ) )
                            return null

                        return (
                            <MeetingCard userRoleForLinks={UserStore.getUser.user_role === "STUDENT" ? "student" : "faculty"} meeting={meeting} key={meeting.id} />
                        )
                    } )}
                </Box>}
                {!loading && !error && OnlineSessionsStore.getOnlineSessions.filter( m => m.meeting_type === sessionType ).length === 0 && <Box display="flex" padding="20px" borderRadius={layout.borderRadius} bgcolor={palette.contentBg} alignItems="center" flexDirection="column" gap="10px" marginTop="10px" >
                    <Box component="img" src={noMeetingsImg} width="200px" alt='No meetings available!' />
                    <Typography variant='subtitle2'>{!meetingType.includes( 'class' ) ? `No ${meetingTypes.find( type => type.value === sessionType ).label} have been scheduled!` : isStaff ? `No ${getTransformedMeetingType( sessionType, { forDisplay: true } )} sessions have been taken yet.` : `No ${getTransformedMeetingType( sessionType, { forDisplay: true } )} were taken on Alive!`}</Typography>
                </Box>}
                {loading && !error && <DataLoadingSpinner waitingMessage="Loading meetings..." />}
                {error && <TryAgainMessageBlock err={error.message} code={error.code} getData={fetchMeetings} loading={refreshing} />}
            </Box>
        </Box>
    )
} )

export default OnlineMeetings